import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { editComboGroupReducer } from './editComboGroupReducer';
import {
	EditComboGroupContext,
	editComboGroupInitialState,
	TEditComboGroupVisibleForm,
	TTypeOfEditComboGroupModalWindow,
} from './editComboGroupContext';
import { EditComboGroupActionTypes } from './editComboGroupActions';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getComboGroupInfo } from 'backend/comboGroups';
import { IItemForGetShortInfo, TLanguageIDs, IDefaultLanguage, IGetComboGroupInfo, IUpdateComboGroup } from 'types';

const EditComboGroupState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editComboGroupReducer, editComboGroupInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const comboGroupID = params.get('comboGroupID');

	const { response, loading, refetch } = useFetchData<IGetComboGroupInfo, string>(
		comboGroupID ? getComboGroupInfo : null,
		comboGroupID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditComboGroupActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useNamedHeader(response?.data.comboGroup.name);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditComboGroupActionTypes.SET_ALL_DATA,
				payload: {
					comboGroupId: response.data.comboGroup.id,
					customer: response.data.comboGroup.customerId,
					restaurants: response.data.comboGroup.restaurants.map((el) => el.restaurantId),
					dishes: response.data.comboGroup.dishes.map((el) => el.id),
					name: response.data.comboGroup.name,
					comboItems: response.data.comboGroup.comboItems.map((el) => el.comboItemId),
					upperLimit: String(response.data.comboGroup.upLimit),
					lowerLimit: String(response.data.comboGroup.downLimit),
					translations: response.data.comboGroup.translations,
				},
			});
			dispatch({
				type: EditComboGroupActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						selectedCustomer: response.data.comboGroup.customerId,
						selectedRestaurants: response.data.comboGroup.restaurants.map((el) => el.restaurantId),
						selectedDishes: response.data.comboGroup.dishes.map((el) => el.id),
						name: response.data.comboGroup.name,
						selectedComboItems: response.data.comboGroup.comboItems.map((el) => el.comboItemId),
						upperLimit: String(response.data.comboGroup.upLimit),
						lowerLimit: String(response.data.comboGroup.downLimit),
						translations: response.data.comboGroup.translations,
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditComboGroupActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TEditComboGroupVisibleForm) => {
		dispatch({
			type: EditComboGroupActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditComboGroupActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: EditComboGroupActionTypes.SET_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setFetchedDishes = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditComboGroupActionTypes.SET_FETCHED_DISHES,
			payload: {
				dishes: value,
			},
		});
	}, []);

	const setDishes = useCallback((value: string[]) => {
		dispatch({
			type: EditComboGroupActionTypes.SET_DISHES,
			payload: {
				dishes: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditComboGroupActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setFetchedComboItems = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditComboGroupActionTypes.SET_FETCHED_COMBO_ITEMS,
			payload: {
				comboItems: value,
			},
		});
	}, []);

	const setComboItems = useCallback((value: string[]) => {
		dispatch({
			type: EditComboGroupActionTypes.SET_COMBO_ITEMS,
			payload: {
				comboItems: value,
			},
		});
	}, []);

	const updateComboItems = useCallback(
		(value: IItemForGetShortInfo[]) => {
			const IDsOfComboItemsShortInfo = value.map((el) => el.id);
			const updatedSelectedComboItems = state.selectedComboItems.filter((el) =>
				IDsOfComboItemsShortInfo.includes(el),
			);
			dispatch({
				type: EditComboGroupActionTypes.SET_COMBO_ITEMS,
				payload: {
					comboItems: updatedSelectedComboItems,
				},
			});
		},
		[state.selectedComboItems],
	);

	const changeUpperLimit = useCallback((value: string) => {
		dispatch({
			type: EditComboGroupActionTypes.CHANGE_UPPER_LIMIT,
			payload: {
				upperLimit: value,
			},
		});
	}, []);

	const changeLowerLimit = useCallback((value: string) => {
		dispatch({
			type: EditComboGroupActionTypes.CHANGE_LOWER_LIMIT,
			payload: {
				lowerLimit: value,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditComboGroupActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditComboGroupActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditComboGroupActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: IUpdateComboGroup | null) => {
		dispatch({
			type: EditComboGroupActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditComboGroupModalWindow) => {
		dispatch({
			type: EditComboGroupActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditComboGroupActionTypes.RESET_FIELDS }), []);

	return (
		<EditComboGroupContext.Provider
			value={{
				refetchData,
				changeVisibleForm,
				setFetchedRestaurants,
				setRestaurants,
				setFetchedDishes,
				setDishes,
				changeName,
				setFetchedComboItems,
				setComboItems,
				updateComboItems,
				changeUpperLimit,
				changeLowerLimit,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				visibleForm: state.visibleForm,
				comboGroupId: state.comboGroupId,
				selectedCustomer: state.selectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				selectedRestaurants: state.selectedRestaurants,
				fetchedDishes: state.fetchedDishes,
				selectedDishes: state.selectedDishes,
				name: state.name,
				fetchedComboItems: state.fetchedComboItems,
				selectedComboItems: state.selectedComboItems,
				upperLimit: state.upperLimit,
				lowerLimit: state.lowerLimit,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditComboGroupContext.Provider>
	);
};

export default EditComboGroupState;
